import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PRODUCT_TITLE } from '@/lib/brand'
import { AuthProvider, useHasDialer, useIsClientRole } from '@/lib/auth/AuthProvider'
import { ProtectedRoute } from '@/lib/auth/ProtectedRoute'
import { PortalRoute } from '@/lib/auth/PortalRoute'
import { AppLayout } from '@/components/layout/AppLayout'
import { PortalLayout } from '@/components/layout/PortalLayout'
import { PortalView } from '@/pages/portal/PortalView'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ProspectsPage } from '@/pages/ProspectsPage'
import { InboundPage } from '@/pages/InboundPage'
import { WarmLeadsPage } from '@/pages/WarmLeadsPage'
import { SequencesPage } from '@/pages/SequencesPage'
import { WorkflowsPage } from '@/pages/WorkflowsPage'
import { AnalyticsLayout } from '@/pages/analytics/AnalyticsLayout'
import { AnalyticsDashboard } from '@/pages/analytics/AnalyticsDashboard'
import { CampaignsView } from '@/pages/analytics/CampaignsView'
import { EmailView } from '@/pages/analytics/EmailView'
import { FunnelView } from '@/pages/analytics/FunnelView'
import { HealthView } from '@/pages/analytics/HealthView'
import { AlertsView } from '@/pages/analytics/AlertsView'
import { VoiceAgentConfig } from '@/pages/VoiceAgentConfig'
import { AgencyDashboard } from '@/pages/AgencyDashboard'
import { TenantSetupConsole } from '@/pages/TenantSetupConsole'
import { ESignPage } from '@/pages/ESignPage'
import { AIDraftsPage } from '@/pages/AIDraftsPage'
import { InboxPage } from '@/pages/InboxPage'
import { IntegrationsPage } from '@/pages/IntegrationsPage'
import { MetaCallbackPage } from '@/pages/MetaCallbackPage'
import { PipelinePage } from '@/pages/PipelinePage'
import { SocialPlannerPage } from '@/pages/SocialPlannerPage'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Toaster } from 'sonner'

function DefaultRedirect() {
  const isClient = useIsClientRole()
  const hasDialer = useHasDialer()
  // SPEC E4 R1 - a CLIENT user landing on `/` goes to the portal, never the
  // operator UI.
  if (isClient) return <Navigate to="/portal" replace />
  return <Navigate to={hasDialer ? '/dashboard' : '/warm-leads'} replace />
}

/**
 * SPEC E4 R1 - wraps the operator UI: a CLIENT-role user who reaches ANY operator
 * route is hard-redirected to /portal. Combined with PortalRoute (which turns
 * non-CLIENT users away from /portal) this makes the two surfaces mutually
 * exclusive per role.
 */
function OperatorOnly({ children }: { children: React.ReactNode }) {
  const isClient = useIsClientRole()
  if (isClient) return <Navigate to="/portal" replace />
  return <>{children}</>
}

function DashboardGuard() {
  const hasDialer = useHasDialer()
  if (!hasDialer) return <Navigate to="/warm-leads" replace />
  return <DashboardPage />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
})

function App() {
  // index.html ships a static <title> that paints before JS runs. It can't read
  // import.meta.env, so the shared bundle would otherwise show the static title
  // on the metabus build too (the metabus build rewrites the static title via a
  // Vite transformIndexHtml plugin, but a runtime set is the belt-and-suspenders
  // for client-side route renders). Set the document title from the build-time
  // brand on mount - defaults keep the buildgravity build at "BuildGravity Sales
  // Engine", metabus build flips to "Metabus Sales Engine".
  useEffect(() => {
    document.title = PRODUCT_TITLE
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          {/* SPEC FE-1 / R3b: a top-level ErrorBoundary so a render error in any
              page (operator or portal) shows a styled fallback instead of a
              white screen. Wires no data; does not change routing access. */}
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* SPEC E4 - the client portal: a separate route tree behind
                PortalRoute (CLIENT-role only) + PortalLayout (no operator nav).
                Views are driven by user.portal_views from /api/me. */}
            <Route
              path="/portal"
              element={
                <PortalRoute>
                  <PortalLayout />
                </PortalRoute>
              }
            >
              <Route index element={<PortalView />} />
              <Route path=":view" element={<PortalView />} />
            </Route>
            <Route
              element={
                <ProtectedRoute>
                  <OperatorOnly>
                    <AppLayout />
                  </OperatorOnly>
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardGuard />} />
              <Route path="/inbound" element={<InboundPage />} />
              <Route path="/warm-leads" element={<WarmLeadsPage />} />
              <Route path="/cold-calling" element={<ProspectsPage />} />
              <Route path="/pipeline" element={<PipelinePage />} />
              <Route path="/sequences" element={<SequencesPage />} />
              <Route path="/social-planner" element={<SocialPlannerPage />} />
              <Route path="/workflows" element={<WorkflowsPage />} />
              <Route path="/analytics" element={<AnalyticsLayout />}>
                <Route index element={<AnalyticsDashboard />} />
                <Route path="campaigns" element={<CampaignsView />} />
                <Route path="email" element={<EmailView />} />
                <Route path="funnel" element={<FunnelView />} />
                <Route path="health" element={<HealthView />} />
                <Route path="alerts" element={<AlertsView />} />
              </Route>
              <Route path="/voice-agent" element={<VoiceAgentConfig />} />
              <Route path="/agency" element={<AgencyDashboard />} />
              <Route path="/agency/setup/:tenantId" element={<TenantSetupConsole />} />
              <Route path="/esign" element={<ESignPage />} />
              <Route path="/ai-drafts" element={<AIDraftsPage />} />
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              {/* Meta/Facebook OAuth callback (META_OAUTH_REDIRECT_URI target). */}
              <Route path="/integrations/meta/callback" element={<MetaCallbackPage />} />
              {/* Redirect old /prospects URL to new /cold-calling */}
              <Route path="/prospects" element={<Navigate to="/cold-calling" replace />} />
            </Route>
            <Route path="/" element={<DefaultRedirect />} />
          </Routes>
          </ErrorBoundary>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
